import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Flex, Spinner, useMediaQuery } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

import {
  FilterChips,
  Footer,
  Header,
  PageWrapper,
  ProjectsGalleryWithPagination,
  SectionTitle,
  Spacer,
  Text,
} from '../../components';
import { Filter } from '../../components/FilterChips';
import { useHeader } from '../../contexts/useHeader';
import { getProjects, projectsLinks } from '../../data/mock.data.projects';
import { Project, ProjectsTypes } from '../../models/Project';

const ProjectsPage = () => {
  const { t } = useTranslation();
  const [isMobile] = useMediaQuery('(max-width: 767px)');
  const { headerDimensions } = useHeader();

  const [projectsData, setProjectsData] = useState<Project[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [activeFilters, setActiveFilters] = useState<string[]>([]);

  useEffect(() => {
    getProjects()
      .then((items: Project[]) => setProjectsData(items))
      .catch((err: unknown) => {
        // eslint-disable-next-line no-console
        console.error('Failed to load projects from Cockpit:', err);
        setProjectsData([]);
      })
      .finally(() => setLoading(false));
  }, []);

  const baseFilters = useMemo<Filter[]>(
    () => [
      {
        label: t('projects.filters.commercial'),
        value: ProjectsTypes.COMMERCIAL,
        selected: false,
      },
      {
        label: t('projects.filters.residential'),
        value: ProjectsTypes.RESIDENTIAL,
        selected: false,
      },
      {
        label: t('projects.filters.concept'),
        value: ProjectsTypes.CONCEPT,
        selected: false,
      },
    ],
    [t]
  );

  const projectFilters = useMemo<Filter[]>(
    () =>
      baseFilters.map((filter: Filter) => ({
        ...filter,
        selected: activeFilters.includes(filter.value),
      })),
    [baseFilters, activeFilters]
  );

  const handleAddFilter = useCallback((filter: Filter) => {
    setActiveFilters((prev) => {
      if (prev.includes(filter.value)) {
        return prev;
      }

      return [...prev, filter.value];
    });
  }, []);

  const handleRemoveFilter = useCallback((filter: Filter) => {
    setActiveFilters((prev) =>
      prev.filter((item) => item !== filter.value)
    );
  }, []);

  const filteredProjects = useMemo<Project[]>(
    () =>
      activeFilters.length > 0
        ? projectsData.filter((project: Project) =>
            activeFilters.includes(project.type)
          )
        : projectsData,
    [activeFilters, projectsData]
  );

  useEffect(() => {
    window.scroll({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  }, []);


  return (
    <>
      <Header links={projectsLinks} justLightMode />

      <PageWrapper>
        <Spacer size={headerDimensions?.height || 0} />

        <SectionTitle title={t('projects.title')} />

        <FilterChips
          filters={projectFilters}
          onSelect={handleAddFilter}
          onRemove={handleRemoveFilter}
          multi
        />

        <Spacer mobileSize={30} size={60} />

        {loading ? (
          <Flex width="100%" justifyContent="center">
            <Spinner />
          </Flex>
        ) : null}

        {!loading && filteredProjects.length > 0 ? (
          <ProjectsGalleryWithPagination
            items={filteredProjects}
            buttonLabel={
              isMobile
                ? t('projects.loadMoreBtn')
                : t('projects.backBtn')
            }
          />
        ) : !loading ? (
          <Text>No projects matching your filters...</Text>
        ) : null}

        <Spacer mobileSize={30} size={40} />

        <Footer />
      </PageWrapper>
    </>
  );
};

export default ProjectsPage;